/**
 * Hydration Error Interceptor
 *
 * React's hydration mismatches (errors #418, #422, #423, #425) are "recoverable" —
 * React silently re-renders the subtree and only logs to console.error.
 * Error boundaries never fire, so production apps lose this signal entirely.
 *
 * This module patches console.error to intercept hydration-related messages
 * and route them through the structured logger → HTTP transport → VictoriaLogs.
 *
 * In production (minified React), the messages look like:
 *   "Minified React error #418; visit https://react.dev/errors/418?args[]=..."
 *
 * In dev, they include the full diff:
 *   "Text content did not match. Server: "Jan 5" Client: "Jan 4""
 *
 * We capture both forms and extract as much info as possible.
 */

import type { Logger } from './logger';
import { buildFingerprint } from './error-identity';
import { reportClientError } from './error-reporting';

/**
 * Strict patterns for React hydration errors.
 *
 * Prod (minified): "Minified React error #418; visit https://react.dev/errors/418..."
 * Dev: "Text content did not match." / "Hydration failed because..." /
 *      "An error occurred during hydration."
 *
 * We anchor on React's exact phrasing to avoid false positives from
 * unrelated errors that happen to contain "#418" or "did not match".
 */
const HYDRATION_PROD_RE = /Minified React error #(418|419|422|423|425)/;
const HYDRATION_DEV_RE =
  /^(Text content did not match|Hydration failed|An error occurred during hydration|There was an error while hydrating|server-rendered HTML)/;

export function isHydrationMessage(message: string): boolean {
  return HYDRATION_PROD_RE.test(message) || HYDRATION_DEV_RE.test(message);
}

export function buildHydrationFingerprint(message: string): string {
  const codeMatch = message.match(/#(\d{3})/);
  const argsMatch = message.match(/args\[\]=([^&\s]*)/g);
  const errorArgs = argsMatch?.map((arg) => decodeURIComponent(arg.replace('args[]=', '')));

  return buildFingerprint([
    'react-hydration',
    codeMatch?.[1] ?? 'unknown',
    ...(errorArgs ?? [message.replace(/\s+/g, ' ').slice(0, 500)]),
  ]);
}

/**
 * Install a console.error interceptor that captures React hydration mismatches.
 * Safe to call multiple times — subsequent calls are no-ops.
 *
 * Only activates when React is using hydrateRoot (SSR). For pure SPA apps
 * using createRoot there is no hydration, so the interceptor stays dormant.
 */
export function installHydrationErrorInterceptor(logger: Logger): void {
  if (typeof window === 'undefined') return;

  if ((window as WindowWithInterceptor).__colix_hydration_interceptor_installed) return;
  (window as WindowWithInterceptor).__colix_hydration_interceptor_installed = true;

  const originalConsoleError = console.error;
  let intercepting = false;

  console.error = function (...args: unknown[]) {
    // Always call the original first
    originalConsoleError.apply(console, args);

    // Guard against re-entrancy: logger.error() calls console.error(),
    // which would trigger this interceptor again → infinite recursion.
    if (intercepting) return;

    // Only match React's actual hydration error format.
    // React 18+ console.error calls for hydration always start with a
    // string containing "Minified React error #NNN" (prod) or specific
    // messages like "Text content did not match" / "Hydration failed" (dev).
    // We require the FIRST argument to be a string matching these patterns
    // to avoid false positives from unrelated errors that happen to contain
    // substrings like "#418".
    const first = args[0];
    if (typeof first !== 'string') return;

    const isHydrationError = isHydrationMessage(first);

    if (!isHydrationError) return;

    intercepting = true;
    try {
      logHydrationError(logger, args);
    } finally {
      intercepting = false;
    }
  };
}

/**
 * Extract React's component stack from console.error arguments.
 *
 * React 18+ passes the component stack as the last string argument that
 * starts with whitespace + "at " (e.g. "\n    at MyComponent\n    at div").
 * In minified prod builds it looks like "\n    at t\n    at div" — still
 * useful for matching against source maps.
 */
function extractComponentStack(args: unknown[]): string | undefined {
  for (let i = args.length - 1; i >= 1; i--) {
    const arg = args[i];
    if (typeof arg === 'string' && /^\s+at /.test(arg)) {
      return arg.trim().slice(0, 2000);
    }
  }
  return undefined;
}

/**
 * Find the first DOM element whose server-rendered text doesn't match its
 * current (client-hydrated) textContent. Walks visible leaf nodes only.
 * Returns an abbreviated CSS-style selector for the mismatched node.
 */
function findMismatchHint(): string | undefined {
  try {
    // React adds data-reactroot or a comment node; the mismatch is usually
    // in the first text node that differs. We can't know the exact node,
    // but we can surface which route component mounted.
    const root = document.getElementById('__next') ?? document.getElementById('root');
    if (!root) return undefined;
    return selectorPath(root);
  } catch {
    return undefined;
  }
}

function selectorPath(el: Element): string {
  const parts: string[] = [];
  let current: Element | null = el;
  while (current && current !== document.body && parts.length < 5) {
    let selector = current.tagName.toLowerCase();
    if (current.id) selector += `#${current.id}`;
    else if (current.className && typeof current.className === 'string') {
      const cls = current.className.split(/\s+/).filter(Boolean).slice(0, 2).join('.');
      if (cls) selector += `.${cls}`;
    }
    parts.unshift(selector);
    current = current.parentElement;
  }
  return parts.join(' > ');
}

function logHydrationError(logger: Logger, args: unknown[]): void {
    const message = args.map(a => String(a)).join(' ');

    // Extract the error code if present
    const codeMatch = message.match(/#(\d{3})/);
    const errorCode = codeMatch ? codeMatch[1] : undefined;

    // Extract server/client diff if available (dev mode)
    const diffMatch = message.match(/Server:\s*"([^"]*)"\s*Client:\s*"([^"]*)"/);

    // Extract args from the minified error URL (prod mode)
    const argsMatch = message.match(/args\[\]=([^&\s]*)/g);
    const errorArgs = argsMatch?.map(a => decodeURIComponent(a.replace('args[]=', '')));

    // React passes a component stack as a trailing string arg
    const componentStack = extractComponentStack(args);
    const domHint = findMismatchHint();

    const summary = `Hydration mismatch${errorCode ? ` [#${errorCode}]` : ''}: ${message.slice(0, 500)}`;

    reportClientError(logger, summary, {
      component: 'react-hydration',
      action: 'hydration_mismatch',
      error_category: 'hydration',
      error_code: errorCode ? `REACT_${errorCode}` : 'REACT_HYDRATION',
      error_fingerprint: buildHydrationFingerprint(message),
      page_url: window.location.href,
      // Dev mode: actual diff
      ...(diffMatch ? {
        hydration_server_value: diffMatch[1],
        hydration_client_value: diffMatch[2],
      } : {}),
      // Prod mode: error arguments (contain the mismatched values)
      ...(errorArgs ? { hydration_error_args: errorArgs.join(', ') } : {}),
      // React component stack — the most useful piece for finding the culprit
      ...(componentStack ? { component_stack: componentStack } : {}),
      // DOM root hint
      ...(domHint ? { dom_root: domHint } : {}),
    }, {
      reporter: 'react-hydration-interceptor',
    });
}

interface WindowWithInterceptor extends Window {
  __colix_hydration_interceptor_installed?: boolean;
}
